import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Archive, ArchiveRestore, ChevronRight, FilePlus2, FileText, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { IconPicker } from './IconPicker'
import { platformApi } from '../api'
import type { Form } from '../types'
import { slugify } from './builder/model'

const statusVariant = { published: 'default', draft: 'secondary', archived: 'outline' } as const
const SLUG_RE = /^[a-z0-9_-]+$/
const PREFIX_RE = /^[A-Z]{2,8}$/

/** Suggest a 2-8 capital-letter number prefix from a form name. */
function suggestPrefix(name: string): string {
    const words = name.toUpperCase().replace(/[^A-Z0-9\s]/g, ' ').split(/\s+/).filter(Boolean)
    const letters = (w: string) => w.replace(/[^A-Z]/g, '')
    let p = words.length > 1 ? words.map((w) => letters(w).slice(0, 1)).join('') : letters(words[0] ?? '').slice(0, 4)
    if (p.length < 2) p = letters(words.join('')).slice(0, 3)
    return p.slice(0, 8)
}

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

function NewFormDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated: (f: Form) => void }) {
    const [name, setName] = useState('')
    const [slug, setSlug] = useState('')
    const [slugEdited, setSlugEdited] = useState(false)
    const [category, setCategory] = useState('')
    const [description, setDescription] = useState('')
    const [icon, setIcon] = useState('')
    const [advancedOpen, setAdvancedOpen] = useState(false)
    const [prefix, setPrefix] = useState('')
    const [prefixEdited, setPrefixEdited] = useState(false)
    const [busy, setBusy] = useState(false)

    const changeName = (v: string) => {
        setName(v)
        if (!slugEdited) setSlug(slugify(v).replace(/_/g, '-'))
        if (!prefixEdited) setPrefix(suggestPrefix(v))
    }
    const slugError = slug === '' ? null : SLUG_RE.test(slug) ? null : 'Use lowercase letters, digits, - or _.'
    const prefixError = prefix === '' || PREFIX_RE.test(prefix) ? null : 'Use 2 to 8 capital letters.'
    const valid = name.trim() !== '' && slug !== '' && !slugError && PREFIX_RE.test(prefix)

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!valid) return
        setBusy(true)
        try {
            const f = await platformApi.createForm({ name: name.trim(), slug, category: category.trim(), description: description.trim(), number_prefix: prefix, icon })
            onCreated(f)
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Could not create the form')
        } finally {
            setBusy(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o) }}>
            <DialogContent>
                <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
                    <DialogHeader>
                        <DialogTitle>New form</DialogTitle>
                        <DialogDescription>You will design its pages and rules next. Nothing is visible to users until you publish.</DialogDescription>
                    </DialogHeader>
                    <FieldGroup>
                        <Field>
                            <FieldLabel htmlFor="nf-name">Name</FieldLabel>
                            <Input id="nf-name" autoFocus value={name} onChange={(e) => changeName(e.target.value)} placeholder="Vendor AI risk review" />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="nf-cat">Category</FieldLabel>
                            <Input id="nf-cat" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Risk" />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="nf-desc">Description</FieldLabel>
                            <Textarea id="nf-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
                        </Field>
                        <Field>
                            <FieldLabel>Icon</FieldLabel>
                            <IconPicker value={icon} onChange={setIcon} form={{ name, slug, category }} />
                        </Field>
                        <Collapsible open={advancedOpen || !!slugError || !!prefixError} onOpenChange={setAdvancedOpen}>
                            <CollapsibleTrigger asChild>
                                <Button type="button" variant="ghost" size="sm" className="-ml-2 text-muted-foreground">
                                    <ChevronRight data-icon="inline-start" className="transition-transform in-data-[state=open]:rotate-90" />
                                    Advanced
                                </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="flex flex-col gap-4 pt-2">
                        <Field data-invalid={!!slugError || undefined}>
                            <FieldLabel htmlFor="nf-slug">Slug</FieldLabel>
                            <Input id="nf-slug" className="font-mono text-xs" value={slug} aria-invalid={!!slugError || undefined}
                                onChange={(e) => { setSlugEdited(true); setSlug(e.target.value) }} />
                            {slugError ? <FieldError>{slugError}</FieldError> : <FieldDescription>Used in links to the form. Cannot be changed later.</FieldDescription>}
                        </Field>
                        <Field data-invalid={!!prefixError || undefined}>
                            <FieldLabel htmlFor="nf-prefix">Number prefix</FieldLabel>
                            <Input id="nf-prefix" className="max-w-40 font-mono uppercase" value={prefix} aria-invalid={!!prefixError || undefined}
                                onChange={(e) => { setPrefixEdited(true); setPrefix(e.target.value.toUpperCase()) }} />
                            {prefixError ? <FieldError>{prefixError}</FieldError> : <FieldDescription>Submissions are numbered like {prefix || 'ABC'}-000001.</FieldDescription>}
                        </Field>
                            </CollapsibleContent>
                        </Collapsible>
                    </FieldGroup>
                    <DialogFooter>
                        <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button type="submit" disabled={!valid || busy}>
                            {busy && <Spinner data-icon="inline-start" />} Create and design
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default function FormsAdminPage() {
    const navigate = useNavigate()
    const [forms, setForms] = useState<Form[] | null>(null)
    const [showIds, setShowIds] = useState(() => {
        try { return localStorage.getItem('forms_show_ids') === '1' } catch { return false }
    })
    const toggleIds = (v: boolean) => {
        setShowIds(v)
        try { localStorage.setItem('forms_show_ids', v ? '1' : '0') } catch { /* optional */ }
    }
    const [newOpen, setNewOpen] = useState(false)
    const [busyId, setBusyId] = useState<string | null>(null)

    useEffect(() => {
        let live = true
        platformApi.listForms(true)
            .then((f) => { if (live) setForms(f) })
            .catch((e: Error) => { toast.error(e.message); if (live) setForms([]) })
        return () => { live = false }
    }, [])

    const sorted = useMemo(() => (forms ?? []).slice().sort((a, b) => b.updated_at.localeCompare(a.updated_at)), [forms])
    const open = (id: string) => void navigate({ to: '/manage/forms/$id', params: { id } })

    const toggleArchive = async (f: Form) => {
        setBusyId(f.id)
        const status = f.status === 'archived' ? (f.active_version ? 'published' : 'draft') : 'archived'
        try {
            // The API overwrites category/description, so send them back unchanged.
            const u = await platformApi.updateForm(f.id, { name: f.name, category: f.category, description: f.description, status })
            setForms((cur) => (cur ?? []).map((x) => (x.id === u.id ? { ...x, ...u } : x)))
            toast.success(status === 'archived' ? `Archived ${f.name}` : `Restored ${f.name}`)
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Update failed')
        } finally {
            setBusyId(null)
        }
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-semibold tracking-tight">Manage forms</h1>
                    <p className="text-sm text-muted-foreground">Design forms, set their rules, and publish new versions.</p>
                </div>
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Switch checked={showIds} onCheckedChange={toggleIds} aria-label="Show form IDs" />
                        Show IDs
                    </label>
                    <Button onClick={() => setNewOpen(true)}><Plus data-icon="inline-start" /> New form</Button>
                </div>
            </div>

            {forms === null ? (
                <div className="flex flex-col gap-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
            ) : sorted.length === 0 ? (
                <Empty className="border border-dashed">
                    <EmptyHeader>
                        <EmptyMedia variant="icon"><FileText /></EmptyMedia>
                        <EmptyTitle>No forms yet</EmptyTitle>
                        <EmptyDescription>Create your first form to start collecting submissions.</EmptyDescription>
                    </EmptyHeader>
                    <EmptyContent>
                        <Button onClick={() => setNewOpen(true)}><FilePlus2 data-icon="inline-start" /> New form</Button>
                    </EmptyContent>
                </Empty>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Version</TableHead>
                            <TableHead className="text-right">Submissions</TableHead>
                            <TableHead>Updated</TableHead>
                            <TableHead><span className="sr-only">Actions</span></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sorted.map((f) => (
                            <TableRow key={f.id} className="cursor-pointer" onClick={() => open(f.id)}>
                                <TableCell>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); open(f.id) }}
                                        className="flex max-w-80 flex-col rounded-sm text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50 lg:max-w-md"
                                    >
                                        <span className="truncate font-medium" title={f.name}>{f.name}</span>
                                        {showIds && <span className="truncate font-mono text-xs text-muted-foreground" title={f.slug}>{f.slug}</span>}
                                    </button>
                                </TableCell>
                                <TableCell className="text-muted-foreground">{f.category || 'General'}</TableCell>
                                <TableCell><Badge variant={statusVariant[f.status] ?? 'outline'} className="capitalize">{f.status}</Badge></TableCell>
                                <TableCell>
                                    <span className="flex items-center gap-1.5">
                                        {f.active_version ? <Badge variant="outline">v{f.active_version}</Badge> : <span className="text-muted-foreground">None</span>}
                                        {f.draft_version && <Badge variant="secondary">Open draft</Badge>}
                                    </span>
                                </TableCell>
                                <TableCell className="text-right tabular-nums">{f.submission_count}</TableCell>
                                <TableCell className="text-muted-foreground">{fmtDate(f.updated_at)}</TableCell>
                                <TableCell className="text-right">
                                    <Button
                                        variant="ghost" size="sm" disabled={busyId === f.id}
                                        onClick={(e) => { e.stopPropagation(); void toggleArchive(f) }}
                                    >
                                        {busyId === f.id ? <Spinner data-icon="inline-start" /> : f.status === 'archived' ? <ArchiveRestore data-icon="inline-start" /> : <Archive data-icon="inline-start" />}
                                        {f.status === 'archived' ? 'Restore' : 'Archive'}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <NewFormDialog
                open={newOpen}
                onOpenChange={setNewOpen}
                onCreated={(f) => { setNewOpen(false); open(f.id) }}
            />
        </div>
    )
}
